import type { ReactNode } from "react";

import { uptimeOf } from "@/components/OltStatus";
import { fmtDateTime, useI18n, type Key } from "@/lib/i18n";
import { HOT_C, isStale, reading, type Reading } from "@/lib/olthealth";
import type { OLT, OLTResources } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * One device-health model for every vendor. Each metric is a normalized
 * reading plus where it came from: the page never cares whether CPU arrived
 * over the CLI (C-Data, HSGQ) or SNMP -- the source is a quiet tag, not a
 * layout. A value that was never reported says so; a stale one says STALE;
 * neither is ever drawn as 0.
 */
type Source = "CLI" | "SNMP" | null;
type Row = { key: string; label: string; reading: Reading; unit?: string; source: Source; tone?: "good" | "warn" | "crit"; text?: string; bar?: boolean };

const UNITS: Record<string, string> = {
  temperature: "°C", voltage: "V", current: "A", fanspeed: "rpm", percent: "%", power: "W", dbm: "dBm", frequency: "Hz",
};

function num(v: number, unit?: string) {
  // Precision by magnitude, trailing zeros dropped: 2%, 4.82%, 24%, 23.8 °C.
  const s = String(Number(v.toFixed(Math.abs(v) >= 100 ? 0 : Math.abs(v) >= 10 ? 1 : 2)));
  return unit === "%" ? `${s}%` : unit ? `${s} ${unit}` : s;
}

export function healthRows(olt: OLT, res: OLTResources | undefined, pollS: number, nowMs: number, tt: (k: Key) => string): Row[] {
  const cli = res?.cli;
  const cliStale = isStale(cli?.updated_at, pollS, nowMs);
  // The resources handler already prefers the CLI reading and fills gaps from SNMP.
  const src = (cliVal: number | null | undefined): Source => (cliVal !== null && cliVal !== undefined ? "CLI" : "SNMP");
  const polledStale = isStale(olt.last_polled_at, pollS, nowMs);
  const reach = olt.status === "reachable" ? "good" : olt.status === "unreachable" ? "crit" : olt.status === "degraded" ? "warn" : undefined;
  const temp = res?.temp_c ?? null;

  const rows: Row[] = [
    { key: "reach", label: tt("olt.h.reach"), reading: { kind: "unavailable" }, source: null, tone: reach,
      text: tt(`olt.reach.${olt.status === "reachable" || olt.status === "unreachable" || olt.status === "degraded" ? olt.status : "unknown"}` as Key) },
    // A failed poll still stamps last_polled_at: unreachable means no fresh data, whatever the time says.
    { key: "tele", label: tt("olt.h.telemetry"), reading: { kind: "unavailable" }, source: null,
      tone: !olt.last_polled_at ? undefined : olt.status === "unreachable" ? "crit" : polledStale ? "warn" : "good",
      text: !olt.last_polled_at ? tt("olt.never") : olt.status === "unreachable" ? tt("olt.tele.noData")
        : polledStale ? tt("olt.tele.stale") : tt("olt.tele.fresh") },
    { key: "up", label: tt("olt.h.uptime"), reading: reading(res?.uptime_s ?? null), source: src(cli?.uptime_s),
      text: res?.uptime_s !== null && res?.uptime_s !== undefined ? uptimeOf(res.uptime_s) : undefined },
    { key: "cpu", label: "CPU", reading: reading(res?.cpu_pct ?? null, cli?.cpu_pct !== null && cli?.cpu_pct !== undefined && cliStale), unit: "%",
      source: src(cli?.cpu_pct), bar: true, tone: (res?.cpu_pct ?? 0) >= 90 ? "crit" : (res?.cpu_pct ?? 0) >= 70 ? "warn" : undefined },
    { key: "mem", label: tt("olt.h.memory"), reading: reading(res?.mem_pct ?? null, cli?.mem_pct !== null && cli?.mem_pct !== undefined && cliStale), unit: "%",
      source: src(cli?.mem_pct), bar: true, tone: (res?.mem_pct ?? 0) >= 90 ? "crit" : (res?.mem_pct ?? 0) >= 80 ? "warn" : undefined },
    { key: "temp", label: tt("olt.h.temp"), reading: reading(temp, cli?.temp_c !== null && cli?.temp_c !== undefined && cliStale), unit: "°C",
      source: src(cli?.temp_c), tone: temp === null ? undefined : temp >= 70 ? "crit" : temp >= HOT_C ? "warn" : undefined },
    // A stale fan state is shown as stale, like any other old reading.
    { key: "fan", label: tt("olt.h.fan"), reading: cliStale && cli?.fan_ok !== null && cli?.fan_ok !== undefined ? { kind: "stale", last: null } : { kind: "unavailable" },
      source: cli?.fan_ok !== null && cli?.fan_ok !== undefined ? "CLI" : null,
      tone: cliStale ? undefined : cli?.fan_ok === false ? "crit" : cli?.fan_ok ? "good" : undefined,
      text: cliStale || cli?.fan_ok === null || cli?.fan_ok === undefined ? undefined
        : `${tt(cli.fan_ok ? "olt.fan.ok" : "olt.fan.fault")}${cli.fan_rpm ? ` · ${cli.fan_rpm} rpm` : ""}` },
    { key: "snmp", label: "SNMP", reading: { kind: "unavailable" }, source: null,
      tone: !res?.monitored ? undefined : res.snmp_status === "1" ? "good" : "crit",
      text: !res?.monitored ? undefined : tt(res.snmp_status === "1" ? "olt.reach.up" : "olt.reach.down") },
  ];
  // A value nobody reported has no source: drop the tag rather than blame SNMP.
  return rows.map((r) => (r.reading.kind === "unavailable" && !r.text ? { ...r, source: null } : r));
}

const TONE = { good: "text-signal-good", warn: "text-signal-warn", crit: "text-signal-crit" };

function Value({ r }: { r: Row }) {
  const { t } = useI18n();
  if (r.text) {
    return (
      <span className={cn("inline-flex items-center gap-1.5", r.tone ? TONE[r.tone] : "text-slate-200")}>
        {r.tone && <span className={cn("size-1.5 rounded-full", r.tone === "good" ? "bg-signal-good" : r.tone === "warn" ? "bg-signal-warn" : "bg-signal-crit")} />}
        {r.text}
      </span>
    );
  }
  if (r.reading.kind === "unavailable") return <span className="text-slate-500">{t("olt.notReported")}</span>;
  if (r.reading.kind === "stale") {
    return (
      <span className="inline-flex items-baseline gap-2">
        <span className="text-slate-500">—</span>
        <span className="rounded-sm bg-amber-400/10 px-1 text-[10.5px] font-semibold tracking-wider text-amber-200">{t("olt.staleTag")}</span>
        {r.reading.last !== null && <span className="text-[11px] text-slate-500">{t("olt.lastValue", { v: num(r.reading.last, r.unit) })}</span>}
      </span>
    );
  }
  return <span className={cn("data", r.tone ? TONE[r.tone] : "text-slate-100")}>{num(r.reading.value, r.unit)}</span>;
}

/** Device health as rows: compact on the Overview, full (with sources) on the Health tab. */
export function DeviceHealth({ rows, compact }: { rows: Row[]; compact?: boolean }) {
  const shown = compact ? rows.filter((r) => r.key !== "snmp" && r.key !== "up") : rows;
  return (
    <dl className="divide-y divide-rule/60">
      {shown.map((r) => (
        <div key={r.key} className="grid grid-cols-[8rem_1fr_auto] items-center gap-3 py-1.5">
          <dt className="text-[12.5px] text-slate-400">{r.label}</dt>
          <dd className="min-w-0 text-[13px]">
            <div className="flex items-center gap-3">
              <Value r={r} />
              {r.bar && r.reading.kind === "value" && (
                <span className="hidden h-1 w-24 overflow-hidden rounded-full bg-rule sm:block" aria-hidden>
                  <span className={cn("block h-full", r.tone === "crit" ? "bg-signal-crit" : r.tone === "warn" ? "bg-signal-warn" : "bg-slate-400")}
                    style={{ width: `${Math.min(100, Math.max(1, r.reading.value))}%` }} />
                </span>
              )}
            </div>
          </dd>
          {!compact && <span className="data text-[10.5px] tracking-wider text-slate-600">{r.source ?? ""}</span>}
        </div>
      ))}
    </dl>
  );
}

/** Environment sensors from the SNMP mirror. Stale sensors never show their stuck value as current. */
export function Sensors({ res }: { res: OLTResources | undefined }) {
  const { t } = useI18n();
  // State sensors report an opaque 0/1 per vendor; a bare "1" beside a temperature reads like a measurement.
  const sensors = (res?.sensors ?? []).filter((s) => s.sensor_class !== "state" && s.sensor_class !== "count");
  if (!sensors.length) return <p className="text-[12.5px] text-slate-500">{t(res?.cli ? "oltRes.cliOnly" : "oltRes.noSensors")}</p>;
  return (
    <ul className="divide-y divide-rule/60">
      {sensors.map((s, i) => {
        const r = reading(s.current, s.stale);
        const unit = UNITS[s.sensor_class] ?? "";
        return (
          <li key={i} className="grid grid-cols-[1fr_auto] items-baseline gap-3 py-1.5">
            <span className="min-w-0 truncate text-[12.5px] text-slate-300">{s.sensor_descr || s.sensor_class}</span>
            <span className="text-right text-[13px]">
              {r.kind === "value" ? (
                <span className={cn("data", s.breach ? "text-signal-crit" : "text-slate-100")}>{num(r.value, unit)}</span>
              ) : r.kind === "stale" ? (
                <span className="inline-flex items-baseline gap-2">
                  <span className="text-slate-500">—</span>
                  <span className="rounded-sm bg-amber-400/10 px-1 text-[10.5px] font-semibold tracking-wider text-amber-200">{t("olt.staleTag")}</span>
                  <span className="text-[11px] text-slate-500">
                    {t("olt.lastReported", { v: num(r.last!, unit), at: s.last_update_at ? fmtDateTime("en-GB", s.last_update_at) : "—" })}
                  </span>
                </span>
              ) : (
                <span className="text-slate-500">{t("olt.notReported")}</span>
              )}
              {r.kind === "value" && s.limit_high !== null && <span className="ml-2 text-[11px] text-slate-500">{t("oltRes.limit")} {s.limit_high}</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** Management facts: kept, but below the telemetry. The driver is implementation detail. */
export function DeviceInfo({ olt, warning }: { olt: OLT; warning: string | null | undefined }) {
  const { t, locale } = useI18n();
  const row = (label: string, value: ReactNode, mono = true) => (
    <div className="grid grid-cols-[8rem_1fr] gap-3 py-1.5">
      <dt className="text-[12.5px] text-slate-400">{label}</dt>
      <dd className={cn("min-w-0 truncate text-[13px] text-slate-200", mono && "data")}>{value}</dd>
    </div>
  );
  return (
    <div>
      <dl className="divide-y divide-rule/60">
        {row(t("olt.info.ip"), `${olt.mgmt_ip}:${olt.mgmt_port}`)}
        {row(t("olt.info.mgmt"), olt.mgmt_protocol.toUpperCase())}
        {row(t("olts.vendor"), olt.vendor || "—", false)}
        {row(t("olts.model"), olt.model || "—")}
        {row(t("olts.firmware"), olt.firmware ?? "—")}
        {row(t("oltDetail.location"), olt.location ?? "—", false)}
      </dl>
      <details className="mt-3 text-[12.5px]">
        <summary className="cursor-pointer text-slate-500 hover:text-slate-300">{t("olt.info.advanced")}</summary>
        <dl className="mt-1 divide-y divide-rule/60">
          {row(t("olts.driver"), olt.driver)}
          {row(t("oltDetail.lastPolled"), fmtDateTime(locale, olt.last_polled_at))}
          {olt.last_error && row(t("olt.info.lastError"), olt.last_error, false)}
          {warning && row(t("olt.info.cliNote"), warning, false)}
        </dl>
      </details>
    </div>
  );
}
